import React, { memo, useState, useMemo } from "react";
import VisitCard from "@/components/Patient/VisitCard";
import { Button } from "@/components/ui/button";
import { Prescription } from "@/types/prescription";
import { Calendar, FileText, Clock, SortDesc } from "lucide-react";
import { cn } from "@/lib/utils";
import { isValid } from "date-fns";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Visit {
  id: string;
  date: string;
  prescription?: Prescription;
  appointment?: any;
}

interface VisitTimelineProps {
  visits: Visit[];
  onEditPrescription?: (prescription: Prescription) => void;
  onViewPrescription?: (prescription: Prescription) => void;
  onCopyPrescription?: (prescription: Prescription) => void;
  loading?: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const parseDate = (dateString: string): Date | null => {
  try {
    const date = new Date(dateString);
    return isValid(date) ? date : null;
  } catch {
    return null;
  }
};

const isValidPrescription = (
    prescription: Prescription | undefined
): prescription is Prescription =>
    !!(prescription?.id && prescription?.date && parseDate(prescription.date));

/**
 * Prescription is editable only if it was created today.
 * Once a patient has left the clinic, the prescription is locked.
 * Doctor should copy and create a new prescription for corrections.
 */
const isPrescriptionEditable = (prescription: Prescription): boolean => {
  if (!prescription.date) return false;
  const prescriptionDate = parseDate(prescription.date);
  if (!prescriptionDate) return false;
  const today = new Date();
  return (
      prescriptionDate.getFullYear() === today.getFullYear() &&
      prescriptionDate.getMonth()    === today.getMonth()    &&
      prescriptionDate.getDate()     === today.getDate()
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

function VisitTimeline({
                         visits,
                         onEditPrescription,
                         onViewPrescription,
                         onCopyPrescription,
                         loading = false,
                       }: VisitTimelineProps) {
  const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc");

  // Valid visits only — computed once from source, not re-derived on sort change
  const validVisits = useMemo(() =>
    visits.filter(
      (v): v is Visit & { prescription: Prescription } =>
        isValidPrescription(v.prescription)
    ),
  [visits]);

  const sortedVisits = useMemo(() =>
    [...validVisits].sort((a, b) => {
      const dateA = parseDate(a.prescription.date);
      const dateB = parseDate(b.prescription.date);
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      return sortOrder === "desc"
        ? dateB.getTime() - dateA.getTime()
        : dateA.getTime() - dateB.getTime();
    }),
  [validVisits, sortOrder]);

  const totalPrescriptions = validVisits.length;

  // Latest prescription ID — derived from source, independent of sort order
  const latestPrescriptionId = useMemo(() => {
    if (validVisits.length === 0) return null;
    return validVisits.reduce((latest, visit) => {
      const d  = parseDate(visit.prescription.date);
      const ld = parseDate(latest.prescription.date);
      if (!d)  return latest;
      if (!ld) return visit;
      return d.getTime() > ld.getTime() ? visit : latest;
    }, validVisits[0]).prescription.id;
  }, [validVisits]);

  // ── Loading ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
        <div className="w-full">
          <div className="flex justify-between items-center mb-6">
            <div className="h-6 bg-muted rounded w-48 animate-pulse" />
            <div className="flex gap-2">
              <div className="h-8 bg-muted rounded w-20 animate-pulse" />
              <div className="h-8 bg-muted rounded w-32 animate-pulse" />
            </div>
          </div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
                <div key={i} className="h-32 bg-muted/50 rounded-lg animate-pulse" />
            ))}
          </div>
        </div>
    );
  }

  // ── Empty ───────────────────────────────────────────────────────────────────

  if (totalPrescriptions === 0) {
    return (
        <div className="w-full">
          <div className="text-center py-10 medical-card">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">
              No prescriptions recorded
            </h3>
            <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
              This patient hasn't had any prescriptions yet.
            </p>
          </div>
        </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
      <div className="w-full">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
          <span className="text-sm text-muted-foreground bg-muted px-2 py-1 rounded-full w-fit">
            {totalPrescriptions} {totalPrescriptions === 1 ? "prescription" : "prescriptions"}
          </span>
          <Button
              variant="outline"
              size="sm"
              onClick={() => setSortOrder(sortOrder === "desc" ? "asc" : "desc")}
              className="flex items-center gap-2"
          >
            <SortDesc className={cn("w-4 h-4 transition-transform", sortOrder === "asc" && "rotate-180")} />
            {sortOrder === "desc" ? "Most Recent" : "Oldest First"}
          </Button>
        </div>

        {/* Timeline */}
        <div className="relative">
          <div className={cn(
              "absolute left-6 top-0 bottom-0 w-0.5",
              sortOrder === "desc"
                  ? "bg-gradient-to-b from-primary/60 via-border to-muted"
                  : "bg-gradient-to-t from-primary/60 via-border to-muted"
          )} />

          <div className="space-y-4 pb-6">
            {sortedVisits.map((visit, index) => {
              const isLatest  = visit.prescription.id === latestPrescriptionId;
              const canEdit   = isPrescriptionEditable(visit.prescription);

              return (
                  <div key={visit.prescription.id} className="relative">
                    {/* Timeline marker — vertically centred with card header (~18px) */}
                    <div className={cn(
                        "absolute left-3 top-[6px] w-6 h-6 border-2 rounded-full flex items-center justify-center shadow-sm z-10 bg-card",
                        isLatest ? "border-primary" : "border-muted-foreground/30"
                    )}>
                      {isLatest
                          ? <Clock    className="w-3 h-3 text-primary" />
                          : <Calendar className="w-3 h-3 text-muted-foreground" />
                      }
                    </div>

                    {/* Visit card */}
                    <div className="ml-12">
                      <VisitCard
                          prescription={visit.prescription}
                          // Only pass onEdit if prescription is from today
                          onEdit={canEdit && onEditPrescription
                              ? onEditPrescription
                              : undefined
                          }
                          onView={onViewPrescription}
                          onCopy={onCopyPrescription}
                          isLatest={isLatest}
                      />
                    </div>
                  </div>
              );
            })}
          </div>

          {/* Timeline end marker */}
          <div className="absolute left-3 bottom-0 w-6 h-6 bg-muted border-2 border-border rounded-full flex items-center justify-center">
            <div className="w-2 h-2 bg-muted-foreground/50 rounded-full" />
          </div>
        </div>
      </div>
  );
}

export default memo(VisitTimeline);